import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { HttpError } from '@rd/types';

@Injectable({ providedIn: 'root' })
export class ErrorMessageHelper {
  constructor(
    private translateService: TranslateService,
    private loyaltyCurrencyService: LoyaltyCurrencyService,
  ) {}

  getErrorCode(error: HttpError): string | undefined {
    return error.errors?.[0].code;
  }

  getErrorMessage(error: HttpError): string | undefined {
    return error.errors?.[0].message;
  }

  getDialogCtaText(error: HttpError): string {
    const erMsg = this.getErrorMessage(error) ?? '';
    switch (this.getErrorCode(error)) {
      case 'GH1010':
        return this.translateService.instant('modal.error.not_eligible.cta');
      case 'PB2202':
        return this.translateService.instant('modal.error.insufficient.cta');
      //  TODO: liase with NN to have better error code and messages
      case 'NN1001':
        if (erMsg.toLowerCase().includes('quantity')) {
          return this.translateService.instant(
            'shopping_cart.gift_card.error.max_items.cta',
          );
        }
      // eslint-disable-next-line no-fallthrough
      default:
        return this.translateService.instant('modal.error.unexpected.cta');
    }
  }

  getErrorDescription(
    error: HttpError,
    interpolateParams: Record<string, string> = {},
  ): string {
    const erMsg = this.getErrorMessage(error) ?? '';
    switch (this.getErrorCode(error)) {
      case 'GH1010':
        return this.translateService.instant(
          'fulfillment.not_eligible_for_redemption',
          {
            currencyShortName:
              this.loyaltyCurrencyService.loyaltyCurrencySelected?.shortName,
          },
        );
        break;
      case 'PB2202':
        return this.translateService.instant('fulfillment.insufficient_miles', {
          currencyShortName:
            this.loyaltyCurrencyService.loyaltyCurrencySelected?.shortName,
        });
        break;
      //  TODO: liase with NN to have better error code and messages
      case 'NN1001':
        if (erMsg.toLowerCase().includes('quantity')) {
          switch (interpolateParams.productType) {
            case 'giftCard':
              return this.translateService.instant(
                'shopping_cart.gift_card.error.max_items.description',
                interpolateParams,
              );
            case 'merchandise':
              return this.translateService.instant(
                'shopping_cart.merchandise.error.max_items.description',
                interpolateParams,
              );
          }
        }

        if (erMsg.toLowerCase().includes('address is invalid')) {
          return this.translateService.instant(
            'shopping_cart.checkout.error.blacklisted_address',
          );
        }
        if (
          erMsg.toLowerCase().includes('country is invalid') ||
          erMsg.toLowerCase().includes('postal code is invalid')
        ) {
          return this.translateService.instant(
            'shopping_cart.checkout.error.invalid_address',
          );
        }
        if (erMsg.toLowerCase().includes('insufficient reward points')) {
          return this.translateService.instant(
            'fulfillment.insufficient_miles',
            {
              currencyShortName:
                this.loyaltyCurrencyService.loyaltyCurrencySelected?.shortName,
            },
          );
        }
        break;
      default:
        return this.translateService.instant('fulfillment.unexpected_error');
    }

    return this.translateService.instant('fulfillment.unexpected_error');
  }

  getErrorTitle(error: HttpError): string {
    const erMsg = this.getErrorMessage(error) ?? '';
    switch (this.getErrorCode(error)) {
      case 'GH1010':
        return this.translateService.instant('fulfillment.generic_error_title');
      case 'PB2202':
        return this.translateService.instant(
          'fulfillment.insufficient_miles_title',
          {
            loyaltyCurrency:
              this.loyaltyCurrencyService.loyaltyCurrencySelected?.shortName,
          },
        );
      //  TODO: liase with NN to have better error code and messages
      case 'NN1001':
        if (erMsg.toLowerCase().includes('quantity')) {
          return this.translateService.instant(
            'shopping_cart.gift_card.error.max_items.title',
          );
        }
        if (erMsg.toLowerCase().includes('insufficient reward points')) {
          return this.translateService.instant(
            'fulfillment.insufficient_miles_title',
            {
              loyaltyCurrency:
                this.loyaltyCurrencyService.loyaltyCurrencySelected?.shortName,
            },
          );
        }

        const invalidAddErrMsgs = [
          'address is invalid',
          'country is invalid',
          'postal code is invalid',
        ];

        if (
          invalidAddErrMsgs.filter((e) => erMsg.toLowerCase().includes(e))
            .length > 0
        ) {
          return this.translateService.instant(
            'shopping_cart.checkout.error.invalid_address_title',
          );
        }

      // eslint-disable-next-line no-fallthrough
      default:
        return this.translateService.instant(
          'fulfillment.unexpected_error_title',
        );
    }
  }

  overrideErrorContent(error: HttpError): {
    title: string;
    message: string;
    ctaText: string;
  } {
    return {
      title: 'fulfillment.unexpected_error_title',
      message: 'fulfillment.unexpected_error',
      ctaText: 'modal.error.unexpected.cta',
    };
  }
}
